import { Action } from '@shopify/app-bridge/actions/Error'
import { getSessionToken as getSessionTokenAppBridge } from '@shopify/app-bridge-utils'
import { useAuthStore } from '@store/authStore'

const BACKEND_BASE_URL = import.meta.env.VITE_BACKEND_BASE_URL
export const CLIENT_AUTH_REDIRECT_ERROR = 'CLIENT_AUTH_REDIRECT_ERROR'
type BuildLoginUrlOptions = {
  shop?: string | null
  requestedScopes?: string[]
  returnTo?: string | null
  topLevel?: boolean
}

/**
 * Decode base64/base64url Shopify `host` param.
 * Format sau decode: `admin.shopify.com/store/{shop}` hoặc `{shop}.myshopify.com/admin`.
 */
const decodeShopifyHost = (host: string): string | null => {
  try {
    const normalized = host.trim().replace(/-/g, '+').replace(/_/g, '/')
    const padding =
      normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4))
    return atob(normalized + padding)
  } catch {
    return null
  }
}

/**
 * Map decoded host → permanent shop domain (`*.myshopify.com`).
 * @business-rule shopify-host-encodes-admin-store-path
 */
const shopDomainFromDecodedHost = (decoded: string): string => {
  const adminMatch = decoded.match(/^admin\.shopify\.com\/store\/([a-z0-9_-]+)/i)
  if (adminMatch?.[1]) {
    return `${adminMatch[1]}.myshopify.com`
  }

  const legacyMatch = decoded.match(/^([a-z0-9][a-z0-9-]*)\.myshopify\.com(?:\/|$)/i)
  if (legacyMatch?.[1]) {
    return `${legacyMatch[1]}.myshopify.com`
  }

  return ''
}

/** App Bridge v4 Config API: `shopify.config.shop` khi đang embed trong admin. */
const getShopFromAppBridgeConfig = (): string => {
  if (typeof window === 'undefined' || typeof shopify === 'undefined') {
    return ''
  }

  const shop = shopify.config?.shop
  return typeof shop === 'string' ? shop.trim() : ''
}

const getHostFromRuntime = (): string => {
  const params = new URLSearchParams(window.location.search)
  return (
    params.get('host') ||
    localStorage.getItem('shopify_host') ||
    useAuthStore.getState().host ||
    ''
  )
}

/**
 * Resolve shop khi reauth trong admin — tránh fallback `/auth/install` dù user đã embed.
 * Ưu tiên: URL → localStorage → authStore → App Bridge config → decode `host`.
 *
 * @fordeer-rule invoice-auth-resolve-shop-before-install-fallback
 * IMPORTANT: Không gọi sau `clearToken()` nếu store/host bị wipe — capture URL trước clear.
 */
const getShopFromRuntime = () => {
  const params = new URLSearchParams(window.location.search)
  const shopFromUrl = params.get('shop')?.trim()
  if (shopFromUrl) {
    return shopFromUrl
  }

  const shopFromStorage = localStorage.getItem('shopify_shop')?.trim()
  if (shopFromStorage) {
    return shopFromStorage
  }

  const shopFromStore = useAuthStore.getState().shop?.trim()
  if (shopFromStore) {
    return shopFromStore
  }

  const shopFromConfig = getShopFromAppBridgeConfig()
  if (shopFromConfig) {
    return shopFromConfig
  }

  const host = getHostFromRuntime()
  if (!host) {
    return ''
  }

  const decoded = decodeShopifyHost(host)
  return decoded ? shopDomainFromDecodedHost(decoded) : ''
}

export const isShopifyAuthFailure = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return false
  }

  const appBridgeError = error as {
    type?: string
    action?: { type?: string }
    message?: string
  }

  return (
    appBridgeError.type === CLIENT_AUTH_REDIRECT_ERROR ||
    appBridgeError.action?.type === CLIENT_AUTH_REDIRECT_ERROR ||
    appBridgeError.message?.includes(CLIENT_AUTH_REDIRECT_ERROR) === true ||
    appBridgeError.type === Action.FAILED_AUTHENTICATION ||
    appBridgeError.action?.type === Action.FAILED_AUTHENTICATION ||
    appBridgeError.message?.includes('Failed to retrieve a session token') === true
  )
}

const getAdminShopSlug = () => {
  const shop = getShopFromRuntime()
  return shop.replace(/\.myshopify\.com$/, '')
}

export const getAdminHomeUrl = () => {
  const adminShopSlug = getAdminShopSlug()
  if (!adminShopSlug) {
    return null
  }

  return `https://admin.shopify.com/store/${adminShopSlug}`
}

/**
 * Backend OAuth login khi session hết hạn.
 * `{BACKEND}/login?shop=…&top_level=true` — không dùng `/auth/install`.
 *
 * @fordeer-rule invoice-session-expired-reauth-via-backend-login
 */
export const getSessionExpiredReauthUrl = (): string | null => {
  if (!BACKEND_BASE_URL) {
    return null
  }

  const shop = getShopFromRuntime()?.trim()
  if (!shop) {
    return null
  }

  const params = new URLSearchParams({
    shop,
    top_level: 'true',
  })
  return `${BACKEND_BASE_URL}/login?${params.toString()}`
}

/** @deprecated Dùng getSessionExpiredReauthUrl — giữ alias cho call site cũ. */
export const getAdminAppReauthUrl = getSessionExpiredReauthUrl

const normalizeRequestedScopes = (requestedScopes?: string[]) =>
  Array.from(new Set((requestedScopes ?? []).map((scope) => scope.trim()).filter(Boolean)))

export const buildLoginUrl = ({
  shop = getShopFromRuntime(),
  requestedScopes,
  returnTo,
  topLevel = true,
}: BuildLoginUrlOptions = {}) => {
  const normalizedScopes = normalizeRequestedScopes(requestedScopes)
  const resolvedShop = (shop || getShopFromRuntime() || '').trim()

  // Chỉ dùng `/auth/install` khi thật sự không biết shop (cài app ngoài admin).
  // Trong admin embed luôn phải có shop → `/login?shop=` để OAuth đúng store.
  if (!resolvedShop) {
    const params = new URLSearchParams()
    if (normalizedScopes.length) {
      params.set('requested_scopes', normalizedScopes.join(','))
    }
    if (returnTo) {
      params.set('return_to', returnTo)
    }
    if (topLevel) {
      params.set('top_level', 'true')
    }

    const query = params.toString()
    return query ? `/auth/install?${query}` : '/auth/install'
  }

  const params = new URLSearchParams({
    shop: resolvedShop,
  })
  if (topLevel) {
    params.set('top_level', 'true')
  }
  if (normalizedScopes.length) {
    params.set('requested_scopes', normalizedScopes.join(','))
  }
  if (returnTo) {
    params.set('return_to', returnTo)
  }

  return `${BACKEND_BASE_URL}/login?${params.toString()}`
}

export const getInstallUrl = () => {
  return buildLoginUrl()
}

type ReauthStatus = 'session_expired' | 'reauth_required' | null

export const normalizeReauthStatus = (status: string | null | undefined): ReauthStatus => {
  if (status === 'session_expired' || status === 'reauth_required') {
    return status
  }

  return null
}

/**
 * URL primary action trên modal auth failure.
 * - `reauth_required` (xin optional scope) → OAuth `/login?requested_scopes=…`
 * - `session_expired` / mặc định → `{BACKEND}/login?shop=…&top_level=true` (không `/auth/install`)
 */
export const resolveAuthFailureInstallUrl = (
  reauthStatus?: string | null,
  preferredUrl?: string | null,
): string => {
  const status = normalizeReauthStatus(reauthStatus)

  if (status === 'reauth_required') {
    return preferredUrl || getInstallUrl()
  }

  const backendLoginUrl = getSessionExpiredReauthUrl()
  if (backendLoginUrl) {
    return backendLoginUrl
  }

  // Không dùng preferred/getInstallUrl nếu chúng là /auth/install (SPA path trong iframe).
  const preferred =
    preferredUrl &&
    !preferredUrl.includes('/auth/install') &&
    preferredUrl.includes('/login')
      ? preferredUrl
      : null
  if (preferred) {
    return preferred
  }

  const adminHome = getAdminHomeUrl()
  if (adminHome) {
    return adminHome
  }

  return 'https://admin.shopify.com/'
}

export const handleShopifyAuthFailure = (error: unknown, reauthStatus?: string | null) => {
  if (!isShopifyAuthFailure(error)) {
    return false
  }

  const store = useAuthStore.getState()
  if (store.authFailure) {
    return true
  }

  const status = normalizeReauthStatus(reauthStatus) ?? 'session_expired'
  // Session expired → backend /login?shop=&top_level=true
  const installUrl = resolveAuthFailureInstallUrl(status)
  const adminHomeUrl = getAdminHomeUrl()

  useAuthStore.setState(
    {
      token: null,
      isLoading: false,
      authFailure: {
        installUrl,
        adminHomeUrl,
        reauthStatus: status,
      },
    },
    false,
  )
  localStorage.removeItem('whoami')

  return true
}

export const getSessionTokenOrRedirect = async (app: Parameters<typeof getSessionTokenAppBridge>[0]) => {
  try {
    return await getSessionTokenAppBridge(app)
  } catch (error) {
    handleShopifyAuthFailure(error)
    throw error
  }
}
